import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase} from '../services/supabase';
import { Product, ProductVariant } from '../types';
import { useNetworkStatus } from './useNetworkStatus';
import { CachedProduct, productCache } from './productDB';

const PAGE_SIZE = 20;
const STALE_TIME = 5 * 60 * 1000;

interface ProductWithVariants extends Product {
    variants: ProductVariant[];
}

interface UseProductsOptions {
    categoryId?: string | null;
    search?: string;
    minPrice?: string;
    maxPrice?: string;
}

interface UseProductsReturn {
    products: ProductWithVariants[];
    total: number;
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    isRevalidating: boolean;
    isOnline: boolean;
    loadMore: () => void;
}

interface CacheEntry {
    items: ProductWithVariants[];
    total: number;
    hasMore: boolean;
    timestamp: number;
    page: number;
}

// 🔥 ГЛОБАЛЬНЫЙ IN-MEMORY КЕШ (вне хука)
const memoryCache = new Map<string, CacheEntry>();

// ✅ Экспортируем функцию очистки кеша
export function clearProductsCache(key?: string) {
    if (key) {
        memoryCache.delete(key);
    } else {
        memoryCache.clear();
    }
}

// ─── Кэш дерева категорий ───
let categoryTreeCache: Map<number, number[]> | null = null;
const productIdsByCategoryCache = new Map<string, number[]>();

async function loadCategoryTree(): Promise<Map<number, number[]>> {
    if (categoryTreeCache) return categoryTreeCache;
    const { data, error } = await supabase
        .from('categories')
        .select('id, parent_id')
        .eq('is_active', true);
    if (error) throw error;
    const map = new Map<number, number[]>();
    for (const { id, parent_id } of data ?? []) {
        if (parent_id != null) {
            if (!map.has(parent_id)) map.set(parent_id, []);
            map.get(parent_id)!.push(id);
        }
    }
    categoryTreeCache = map;
    return map;
}

function getAllDescendantIds(rootId: number, tree: Map<number, number[]>): number[] {
    const result: number[] = [rootId];
    const stack: number[] = [rootId];
    while (stack.length > 0) {
        const current = stack.pop()!;
        for (const child of tree.get(current) ?? []) {
            if (!result.includes(child)) {
                result.push(child);
                stack.push(child);
            }
        }
    }
    return result;
}

async function getProductIdsByCategory(categoryId: number): Promise<number[]> {
    const cacheKey = `cat_${categoryId}`;
    if (productIdsByCategoryCache.has(cacheKey)) {
        return productIdsByCategoryCache.get(cacheKey)!;
    }
    const tree = await loadCategoryTree();
    const allCategoryIds = getAllDescendantIds(categoryId, tree);
    const { data, error } = await supabase
        .from('product_categories')
        .select('product_id')
        .in('category_id', allCategoryIds);
    if (error) throw error;
    const ids = (data ?? []).map((row) => row.product_id);
    productIdsByCategoryCache.set(cacheKey, ids);
    return ids;
}

// ─── НОРМАЛИЗАЦИЯ ТЕКСТА ───
function normalizeRussianText(text: string): string {
    if (!text) return '';
    return text.toLowerCase().replace(/ё/g, 'е').replace(/Ё/g, 'е');
}

function normalizeSearchTerm(searchTerm: string): string {
    let normalized = searchTerm.trim();
    normalized = normalized.replace(/^ART-/i, '');
    return normalized;
}

function getRussianGender(gender: string): string {
    const genderMap: Record<string, string> = {
        'male': 'мужской',
        'female': 'женский',
        'unisex': 'унисекс',
        'children': 'детский'
    };
    return genderMap[gender] || gender;
}

function getRussianSeason(season: string): string {
    const seasonMap: Record<string, string> = {
        'spring': 'весна',
        'summer': 'лето',
        'autumn': 'осень',
        'winter': 'зима',
        'demi': 'демисезон',
        'all': 'всесезонный'
    };
    return seasonMap[season] || season;
}

function buildSearchQuery(query: any, searchTerm: string) {
    if (!searchTerm?.trim()) return query;

    const rawSearch = searchTerm.trim();
    const normalizedSearch = normalizeSearchTerm(rawSearch);
    const searchLower = rawSearch.toLowerCase();
    const searchWithE = searchLower.replace(/ё/g, 'е');
    const searchWithYo = searchLower.replace(/е/g, 'ё');

    const conditions: string[] = [];

    conditions.push(`lower(name) like '%${searchLower}%'`);
    conditions.push(`lower(name) like '%${searchWithE}%'`);
    conditions.push(`lower(name) like '%${searchWithYo}%'`);
    conditions.push(`lower(brand) like '%${searchLower}%'`);
    conditions.push(`lower(brand) like '%${searchWithE}%'`);
    conditions.push(`lower(brand) like '%${searchWithYo}%'`);
    conditions.push(`lower(color) like '%${searchLower}%'`);
    conditions.push(`lower(color) like '%${searchWithE}%'`);
    conditions.push(`lower(color) like '%${searchWithYo}%'`);
    conditions.push(`lower(country) like '%${searchLower}%'`);
    conditions.push(`lower(country) like '%${searchWithE}%'`);
    conditions.push(`lower(country) like '%${searchWithYo}%'`);
    conditions.push(`lower(material) like '%${searchLower}%'`);
    conditions.push(`lower(material) like '%${searchWithE}%'`);
    conditions.push(`lower(material) like '%${searchWithYo}%'`);
    conditions.push(`product_code ilike '%${rawSearch}%'`);
    conditions.push(`product_code ilike '%${normalizedSearch}%'`);
    conditions.push(`product_code ilike 'ART-${normalizedSearch}'`);
    conditions.push(`product_code ilike '${normalizedSearch}'`);

    if (searchLower.includes('муж') || searchLower.includes('мужской')) {
        conditions.push(`gender = 'male'`);
    }
    if (searchLower.includes('жен') || searchLower.includes('женский')) {
        conditions.push(`gender = 'female'`);
    }
    if (searchLower.includes('унисекс')) {
        conditions.push(`gender = 'unisex'`);
    }
    if (searchLower.includes('дет') || searchLower.includes('детский')) {
        conditions.push(`gender = 'children'`);
    }
    if (searchLower.includes('весн')) {
        conditions.push(`season = 'spring'`);
    }
    if (searchLower.includes('лет')) {
        conditions.push(`season = 'summer'`);
    }
    if (searchLower.includes('осен')) {
        conditions.push(`season = 'autumn'`);
    }
    if (searchLower.includes('зим')) {
        conditions.push(`season = 'winter'`);
    }
    if (searchLower.includes('демисезон')) {
        conditions.push(`season = 'demi'`);
    }
    if (searchLower.includes('всесезон')) {
        conditions.push(`season = 'all'`);
    }

    return query.or(conditions.join(','));
}

function matchesSearch(fieldValue: string, searchTerm: string): boolean {
    if (!fieldValue) return false;
    const normalizedField = normalizeRussianText(fieldValue);
    const normalizedSearch = normalizeRussianText(searchTerm);
    return normalizedField.includes(normalizedSearch);
}

function filterCachedProducts(
    cached: CachedProduct[],
    options: UseProductsOptions,
    allowedIds: number[] | null,
): CachedProduct[] {
    let result = cached.filter((p) => p.status === 'approved');

    if (allowedIds) {
        const idSet = new Set(allowedIds);
        result = result.filter((p) => idSet.has(p.id));
    }

    if (options.search?.trim()) {
        const rawSearch = options.search.trim();
        const normalizedSearch = normalizeSearchTerm(rawSearch);
        const searchLower = normalizeRussianText(rawSearch);

        let genderMatches: string[] = [];
        let seasonMatches: string[] = [];

        if (searchLower.includes('муж') || searchLower.includes('мужской')) {
            genderMatches.push('male', 'мужской');
        }
        if (searchLower.includes('жен') || searchLower.includes('женский')) {
            genderMatches.push('female', 'женский');
        }
        if (searchLower.includes('унисекс')) {
            genderMatches.push('unisex', 'унисекс');
        }
        if (searchLower.includes('дет') || searchLower.includes('детский')) {
            genderMatches.push('children', 'детский');
        }
        if (searchLower.includes('весн')) {
            seasonMatches.push('spring', 'весна');
        }
        if (searchLower.includes('лет')) {
            seasonMatches.push('summer', 'лето');
        }
        if (searchLower.includes('осен')) {
            seasonMatches.push('autumn', 'осень');
        }
        if (searchLower.includes('зим')) {
            seasonMatches.push('winter', 'зима');
        }
        if (searchLower.includes('демисезон')) {
            seasonMatches.push('demi', 'демисезон');
        }
        if (searchLower.includes('всесезон')) {
            seasonMatches.push('all', 'всесезонный');
        }

        result = result.filter((p) => {
            const productCode = p.product_code || '';
            const normalizedCode = normalizeRussianText(productCode.replace(/^ART-/i, ''));
            const name = p.name || '';
            const brand = p.brand || '';
            const color = p.color || '';
            const country = p.country || '';
            const material = p.material || '';
            const gender = p.gender || '';
            const season = p.season || '';

            if (normalizedCode === normalizedSearch) return true;
            if (matchesSearch(productCode, rawSearch)) return true;
            if (matchesSearch(name, rawSearch)) return true;
            if (matchesSearch(brand, rawSearch)) return true;
            if (matchesSearch(color, rawSearch)) return true;
            if (matchesSearch(country, rawSearch)) return true;
            if (matchesSearch(material, rawSearch)) return true;

            if (genderMatches.length > 0) {
                const russianGender = getRussianGender(gender);
                const normalizedGender = normalizeRussianText(gender);
                const normalizedRussianGender = normalizeRussianText(russianGender);
                for (const g of genderMatches) {
                    const normalizedG = normalizeRussianText(g);
                    if (normalizedG === normalizedGender || normalizedG === normalizedRussianGender) {
                        return true;
                    }
                }
            }

            if (seasonMatches.length > 0) {
                const russianSeason = getRussianSeason(season);
                const normalizedSeason = normalizeRussianText(season);
                const normalizedRussianSeason = normalizeRussianText(russianSeason);
                for (const s of seasonMatches) {
                    const normalizedS = normalizeRussianText(s);
                    if (normalizedS === normalizedSeason || normalizedS === normalizedRussianSeason) {
                        return true;
                    }
                }
            }

            return false;
        });
    }

    if (options.minPrice) {
        const min = parseFloat(options.minPrice);
        result = result.filter((p) => {
            const price = (p as any).variants?.[0]?.price ?? 0;
            return price >= min;
        });
    }

    if (options.maxPrice) {
        const max = parseFloat(options.maxPrice);
        result = result.filter((p) => {
            const price = (p as any).variants?.[0]?.price ?? 0;
            return price <= max;
        });
    }

    result.sort((a, b) =>
        new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
    );

    return result;
}

export const useProducts = (options: UseProductsOptions): UseProductsReturn => {
    const isOnline = useNetworkStatus();
    const isOnlineRef = useRef(isOnline);
    useEffect(() => { isOnlineRef.current = isOnline; }, [isOnline]);

    const [products, setProducts] = useState<ProductWithVariants[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [isRevalidating, setIsRevalidating] = useState(false);

    const pageRef = useRef(0);
    const isFetchingRef = useRef(false);
    const isMountedRef = useRef(true);
    const optionsRef = useRef(options);
    const allowedCategoryIdsRef = useRef<number[] | null>(null);

    const optionsKey = useMemo(() => JSON.stringify({
        categoryId: options.categoryId,
        search: options.search,
        minPrice: options.minPrice,
        maxPrice: options.maxPrice,
    }), [options.categoryId, options.search, options.minPrice, options.maxPrice]);

    useEffect(() => { optionsRef.current = options; }, [options]);

    const resolveAllowedIds = useCallback(async (): Promise<number[] | null> => {
        if (!optionsRef.current.categoryId) return null;
        if (allowedCategoryIdsRef.current) return allowedCategoryIdsRef.current;
        try {
            const ids = await getProductIdsByCategory(
                parseInt(optionsRef.current.categoryId, 10)
            );
            allowedCategoryIdsRef.current = ids;
            return ids;
        } catch {
            return [];
        }
    }, []);

    const fetchPage = useCallback(async (page: number): Promise<{
        items: ProductWithVariants[];
        total: number;
        hasMore: boolean;
    }> => {
        const { search, minPrice, maxPrice } = optionsRef.current;
        const allowedIds = await resolveAllowedIds();

        if (optionsRef.current.categoryId && allowedIds?.length === 0) {
            return { items: [], total: 0, hasMore: false };
        }

        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        let query = supabase
            .from('products')
            .select('*, variants:product_variants(*)', { count: 'exact' })
            .eq('status', 'approved')
            .order('created_at', { ascending: false })
            .range(from, to);

        if (allowedIds && allowedIds.length > 0) {
            query = query.in('id', allowedIds.slice(0, 1000));
        }

        if (search?.trim()) {
            query = buildSearchQuery(query, search);
        }

        if (minPrice) query = query.gte('min_price', parseFloat(minPrice));
        if (maxPrice) query = query.lte('min_price', parseFloat(maxPrice));

        const { data, error, count } = await query;
        if (error) throw error;

        const items = (data ?? []) as ProductWithVariants[];
        const serverTotal = count ?? 0;
        const nextOffset = (page + 1) * PAGE_SIZE;
        const serverHasMore = nextOffset < serverTotal && items.length === PAGE_SIZE;

        return { items, total: serverTotal, hasMore: serverHasMore };
    }, [resolveAllowedIds]);

    const saveToDB = useCallback(async (items: ProductWithVariants[]) => {
        if (items.length === 0) return;
        const now = Date.now();
        const withMeta: CachedProduct[] = items.map((p) => ({ ...p, _cachedAt: now }));
        try {
            await productCache.upsertMany(withMeta);
        } catch (e) {
            console.warn('[useProducts] DB save error:', e);
        }
    }, []);

    const loadFromDB = useCallback(async () => {
        try {
            const allCached = await productCache.getAll();
            if (allCached.length === 0) {
                setLoading(false);
                return;
            }
            const allowedIds = await resolveAllowedIds();
            const filtered = filterCachedProducts(allCached, optionsRef.current, allowedIds);
            if (isMountedRef.current) {
                setProducts(filtered as ProductWithVariants[]);
                setTotal(filtered.length);
                setHasMore(false);
                setLoading(false);
            }
        } catch (e) {
            console.warn('[useProducts] DB load error:', e);
            if (isMountedRef.current) setLoading(false);
        }
    }, [resolveAllowedIds]);

    // 🔥 ОСНОВНОЙ ЭФФЕКТ
    useEffect(() => {
        pageRef.current = 0;
        allowedCategoryIdsRef.current = null;
        isFetchingRef.current = false;
        isMountedRef.current = true;

        const cached = memoryCache.get(optionsKey);

        if (cached) {
            setProducts(cached.items);
            setTotal(cached.total);
            setHasMore(cached.hasMore);
            pageRef.current = cached.page;

            // Кеш свежий — не делаем запрос
            if (Date.now() - cached.timestamp < STALE_TIME) {
                setLoading(false);
                setIsRevalidating(false);
                return;
            }

            // Кеш устарел — показываем старые данные, обновляем в фоне
            setLoading(false);
            setIsRevalidating(true);
        } else {
            // ✅ Нет кеша — показываем загрузку
            setProducts([]);
            setTotal(0);
            setHasMore(true);
            setLoading(true);
        }

        if (!isOnlineRef.current) {
            if (!cached) loadFromDB();
            return;
        }

        let cancelled = false;

        const init = async () => {
            try {
                isFetchingRef.current = true;
                const { items, total: serverTotal, hasMore: more } = await fetchPage(0);

                if (cancelled || !isMountedRef.current) return;

                memoryCache.set(optionsKey, {
                    items,
                    total: serverTotal,
                    hasMore: more,
                    timestamp: Date.now(),
                    page: 0,
                });

                setProducts(items);
                setTotal(serverTotal);
                setHasMore(more);
                setLoading(false);
                setIsRevalidating(false);

                saveToDB(items);
            } catch (err: any) {
                if (cancelled) return;
                console.error('[useProducts] init error:', err?.message);
                if (!cached) await loadFromDB();
                setLoading(false);
                setIsRevalidating(false);
            } finally {
                isFetchingRef.current = false;
            }
        };

        init();

        return () => {
            cancelled = true;
            isMountedRef.current = false;
        };
    }, [optionsKey, isOnline]);

    // При восстановлении сети
    useEffect(() => {
        if (!isOnline) return;
        if (isFetchingRef.current) return;
        if (!isMountedRef.current) return;

        const cached = memoryCache.get(optionsKey);
        if (cached && Date.now() - cached.timestamp < STALE_TIME) return;

        setIsRevalidating(true);

        const revalidate = async () => {
            try {
                isFetchingRef.current = true;
                const { items, total: serverTotal, hasMore: more } = await fetchPage(0);
                if (!isMountedRef.current) return;

                memoryCache.set(optionsKey, {
                    items,
                    total: serverTotal,
                    hasMore: more,
                    timestamp: Date.now(),
                    page: 0,
                });

                setProducts(items);
                setTotal(serverTotal);
                setHasMore(more);
                pageRef.current = 0;
                saveToDB(items);
            } catch (e) {
                console.warn('[useProducts] revalidate error:', e);
            } finally {
                isFetchingRef.current = false;
                if (isMountedRef.current) setIsRevalidating(false);
            }
        };

        const timer = setTimeout(revalidate, 100);
        return () => clearTimeout(timer);
    }, [isOnline, optionsKey, fetchPage, saveToDB]);

    const loadMore = useCallback(async () => {
        if (!hasMore || loadingMore || loading || isFetchingRef.current) return;
        if (!isOnlineRef.current) return;

        const nextPage = pageRef.current + 1;
        setLoadingMore(true);
        isFetchingRef.current = true;

        try {
            const { items, total: serverTotal, hasMore: more } = await fetchPage(nextPage);
            if (!isMountedRef.current) return;

            saveToDB(items);
            pageRef.current = nextPage;

            setProducts((prev) => {
                const updated = [...prev, ...items];
                const cached = memoryCache.get(optionsKey);
                if (cached) {
                    memoryCache.set(optionsKey, {
                        ...cached,
                        items: updated,
                        total: serverTotal,
                        hasMore: more,
                        timestamp: Date.now(),
                        page: nextPage,
                    });
                }
                return updated;
            });

            setTotal(serverTotal);
            setHasMore(more);
        } catch (err: any) {
            console.error('[useProducts] loadMore error:', err?.message);
        } finally {
            isFetchingRef.current = false;
            if (isMountedRef.current) setLoadingMore(false);
        }
    }, [hasMore, loadingMore, loading, fetchPage, saveToDB, optionsKey]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    return {
        products,
        total,
        loading,
        loadingMore,
        hasMore,
        isRevalidating,
        isOnline,
        loadMore,
    };
};

export default useProducts;
